import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { Booking, BookingInsert, BookingUpdate, BookingWithDetails } from '@/lib/integrations/supabase/types';
import type { BookedRange } from '@/lib/features/swr';
import { authenticatedPost, authenticatedDelete } from '@/lib/core/api/authenticated-client';

/**
 * Computes which time slots are disabled (unavailable) based on existing bookings and optionally past times.
 * A slot is disabled if:
 * - The proposed booking [slotStart, slotEnd) overlaps any existing booking, OR
 * - The slot is in the past (when currentTime is provided and date is today)
 */
export function computeDisabledSlots(
  timeSlots: string[],
  bookedRanges: BookedRange[],
  selectedDate: Date,
  durationMinutes: number,
  currentTime?: Date
): {
  disabledSlots: Set<string>;
  disabledReasons: Map<string, 'past' | 'booked'>;
} {
  const disabledSlots = new Set<string>();
  const disabledReasons = new Map<string, 'past' | 'booked'>();

  if (!selectedDate || timeSlots.length === 0) {
    return { disabledSlots, disabledReasons };
  }

  const now = currentTime || new Date();

  const isToday =
    selectedDate.getFullYear() === now.getFullYear() &&
    selectedDate.getMonth() === now.getMonth() &&
    selectedDate.getDate() === now.getDate();

  const bookedIntervals = bookedRanges.map(range => ({
    start: new Date(range.start_at),
    end: new Date(range.end_at),
  }));

  for (const slot of timeSlots) {
    const [hours, minutes] = slot.split(':').map(Number);

    const slotStart = new Date(selectedDate);
    slotStart.setHours(hours, minutes, 0, 0);

    if (isToday && slotStart < now) {
      disabledSlots.add(slot);
      disabledReasons.set(slot, 'past');
      continue;
    }

    const slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + durationMinutes);

    if (bookedRanges.length > 0) {
      const hasOverlap = bookedIntervals.some(booking =>
        slotStart < booking.end && slotEnd > booking.start
      );

      if (hasOverlap) {
        disabledSlots.add(slot);
        disabledReasons.set(slot, 'booked');
      }
    }
  }

  return { disabledSlots, disabledReasons };
}

/**
 * Gets valid time slots that can accommodate a booking of the specified duration.
 * A slot is valid if:
 * - The proposed booking [slotStart, slotEnd) does NOT overlap any existing booking, AND
 * - The slot is not in the past (when currentTime is provided and date is today)
 *
 * This function filters out invalid slots entirely (hides them) rather than disabling them.
 */
export function getValidTimeSlots(
  timeSlots: Array<{ value: string; label: string }>,
  bookedRanges: BookedRange[],
  selectedDate: Date,
  durationMinutes: number,
  currentTime?: Date
): Array<{ value: string; label: string }> {
  if (!selectedDate || timeSlots.length === 0) {
    return [];
  }

  const now = currentTime || new Date();

  const isToday =
    selectedDate.getFullYear() === now.getFullYear() &&
    selectedDate.getMonth() === now.getMonth() &&
    selectedDate.getDate() === now.getDate();

  const bookedIntervals = bookedRanges.map(range => ({
    start: new Date(range.start_at),
    end: new Date(range.end_at),
  }));

  return timeSlots.filter(slot => {
    const [hours, minutes] = slot.value.split(':').map(Number);

    const slotStart = new Date(selectedDate);
    slotStart.setHours(hours, minutes, 0, 0);

    if (isToday && slotStart < now) {
      return false;
    }

    const slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + durationMinutes);

    if (bookedRanges.length > 0) {
      const hasOverlap = bookedIntervals.some(booking =>
        slotStart < booking.end && slotEnd > booking.start
      );

      if (hasOverlap) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get bookings for a specific date.
 */
export async function getBookingsForDate(
  businessId: string,
  date: Date,
  staffId?: string | null
): Promise<BookingWithDetails[]> {
  const supabase = getBrowserClient();

  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  let query = supabase
    .from('bookings')
    .select(`
      *,
      customer:customers(*),
      staff_member:staff_members(*),
      booking_services(
        *,
        service:services(*)
      )
    `)
    .eq('business_id', businessId)
    .gte('start_at', startOfDay.toISOString())
    .lte('start_at', endOfDay.toISOString())
    .neq('status', 'cancelled')
    .order('start_at', { ascending: true });

  if (staffId) {
    query = query.eq('staff_id', staffId);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data || []) as BookingWithDetails[];
}

/**
 * Get bookings for a date range (for week/month views).
 */
export async function getBookingsForDateRange(
  businessId: string,
  startDate: Date,
  endDate: Date,
  staffId?: string | null
): Promise<BookingWithDetails[]> {
  const supabase = getBrowserClient();

  let query = supabase
    .from('bookings')
    .select(`
      *,
      customer:customers(*),
      staff_member:staff_members(*),
      booking_services(
        *,
        service:services(*)
      )
    `)
    .eq('business_id', businessId)
    .gte('start_at', startDate.toISOString())
    .lte('start_at', endDate.toISOString())
    .neq('status', 'cancelled')
    .order('start_at', { ascending: true });

  if (staffId) {
    query = query.eq('staff_id', staffId);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data || []) as BookingWithDetails[];
}

/**
 * Get a single booking by ID.
 */
export async function getBooking(id: string): Promise<BookingWithDetails | null> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      customer:customers(*),
      staff_member:staff_members(*),
      booking_services(
        *,
        service:services(*)
      )
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as BookingWithDetails;
}

/**
 * Get upcoming bookings (next N).
 */
export async function getUpcomingBookings(
  businessId: string,
  limit: number = 5
): Promise<BookingWithDetails[]> {
  const supabase = getBrowserClient();
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      customer:customers(*),
      staff_member:staff_members(*),
      booking_services(
        *,
        service:services(*)
      )
    `)
    .eq('business_id', businessId)
    .gte('start_at', now)
    .neq('status', 'cancelled')
    .order('start_at', { ascending: true })
    .limit(limit);

  if (error) {
    throw error;
  }

  return (data || []) as BookingWithDetails[];
}

/**
 * Check if a time slot is available for a staff member.
 */
export async function isTimeSlotAvailable(
  businessId: string,
  staffId: string,
  startAt: Date,
  endAt: Date,
  excludeBookingId?: string
): Promise<boolean> {
  const supabase = getBrowserClient();

  const now = new Date();

  let query = supabase
    .from('bookings')
    .select('id')
    .eq('business_id', businessId)
    .eq('staff_id', staffId)
    .in('status', ['pending', 'confirmed', 'in_queue', 'in_progress'])
    .gte('end_at', now.toISOString())
    .or(`and(start_at.lt.${endAt.toISOString()},end_at.gt.${startAt.toISOString()})`);

  if (excludeBookingId) {
    query = query.neq('id', excludeBookingId);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data?.length || 0) === 0;
}

export interface CreateBookingData {
  businessId: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  staffId?: string;
  startAt: string;
  endAt: string;
  services: { serviceId: string; priceSnapshot: number; quantity?: number; originalPrice?: number }[];
  notes?: string;
  isWalkIn?: boolean;
  paymentMethod?: 'cash' | 'card' | 'transfer';
  customTotal?: number;
}

/**
 * Create a new booking via Route Handler.
 */
export async function createBooking(data: CreateBookingData): Promise<Booking> {
  return authenticatedPost<Booking>('/api/bookings/create', data);
}

/**
 * Cancel a booking via Route Handler.
 * This deletes the booking and all associated records (payments, booking_services, alerts).
 * Customer records are preserved.
 */
export async function cancelBooking(bookingId: string): Promise<void> {
  await authenticatedPost(`/api/bookings/${bookingId}/cancel`, {});
}

/**
 * Delete a booking via Route Handler.
 * This permanently deletes the booking and all associated records (payments, booking_services, alerts).
 * Customer records are preserved.
 */
export async function deleteBooking(bookingId: string): Promise<void> {
  await authenticatedDelete(`/api/bookings/${bookingId}`);
}

/**
 * Reschedule a booking via Route Handler.
 */
export async function rescheduleBooking(
  bookingId: string,
  newStartAt: string,
  newEndAt: string
): Promise<Booking> {
  return authenticatedPost<Booking>(`/api/bookings/${bookingId}/reschedule`, {
    startAt: newStartAt,
    endAt: newEndAt,
  });
}

/**
 * Send a reminder for a booking via Route Handler.
 */
export async function sendBookingReminder(bookingId: string): Promise<void> {
  const response = await fetch(`/api/bookings/${bookingId}/send-reminder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send reminder');
  }
}

/**
 * Mark a booking as confirmed via Route Handler.
 */
export async function confirmBooking(bookingId: string, token?: string): Promise<void> {
  const response = await fetch(`/api/bookings/${bookingId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to confirm booking');
  }
}

/**
 * Mark a booking as confirmed & paid via Route Handler.
 * For cash payments, cashDetails should include subtotal, vatRate, and cashReceived.
 * Optionally accepts services array and customTotal for service updates and price overrides.
 */
export async function markBookingPaid(
  bookingId: string,
  method: 'cash' | 'card' | 'transfer' = 'cash',
  notes?: string,
  cashDetails?: { subtotal: number; vatRate: number; cashReceived: number },
  services?: { serviceId: string; priceSnapshot: number; quantity?: number }[],
  customTotal?: number
): Promise<{ booking: Booking; payment: unknown }> {
  return authenticatedPost<{ booking: Booking; payment: unknown }>(
    `/api/bookings/${bookingId}/mark-paid`,
    {
      method,
      notes,
      ...(cashDetails && { cashDetails }),
      ...(services && { services }),
      ...(customTotal !== undefined && { customTotal }),
    }
  );
}

/**
 * Request customer confirmation via email/SMS.
 */
export async function requestBookingConfirmation(bookingId: string): Promise<{ message: string }> {
  const response = await fetch(`/api/bookings/${bookingId}/request-confirmation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send confirmation request');
  }

  return response.json();
}

/**
 * Mark a booking as completed (service finished) via Route Handler.
 */
export async function completeBooking(bookingId: string): Promise<Booking> {
  const data = await authenticatedPost<{ booking: Booking }>(
    `/api/bookings/${bookingId}/complete`,
    {}
  );
  return data.booking;
}
